/**
 * ceoReportText — the structured CEO report, as plain text for the clipboard.
 *
 * Card P201 item 5 ("export: ... and copy-as-text").
 *
 * ⛔⛔ IT READS THE SAME FUNCTIONS THE SCREEN READS. kpiTiles(), formatMetric()
 * and formatDelta() are imported from CeoReportView rather than re-derived
 * here, so a tile cannot say $1.44M on screen and $1,435,902 in the paste, and
 * a KPI dropped on screen (untracked) cannot reappear in the text. Card P197
 * STEP 0 established the rule for the markdown path — one parse, two emitters
 * — and this is the same rule for the structured path: one source of numbers,
 * two renderings.
 *
 * ⚠ The section NUMBER is the index, exactly as on screen. The model does not
 * number its sections any more and the parser strips an ordinal if one is
 * typed, so these numbers are generated in both places from the same array
 * order and cannot drift apart.
 *
 * ⚠ A chart cannot be pasted. It is named, with the payload key it drew from,
 * so the reader of the text knows a figure existed and where it came from —
 * silently omitting it would make the text claim to be the whole report.
 */

import { kpiTiles, formatMetric, formatDelta } from './CeoReportView';
import {
  comparisonCaption, formatRangeLabel, formatGeneratedAt, stripStrayMarkdown,
} from './_reportFormat';

/**
 * Strip markdown; the text has no weight to give it.
 *
 * ⚠ Card P214: this used to unwrap only PAIRED `**`, so an unclosed `**` or a
 * single `*` survived into the clipboard — the same leak the screen had, one
 * surface sideways. It now runs the pair-unwrap and then the SAME stray-star
 * strip the view uses, so a paste and the page agree character for character.
 */
const plain = (s) => stripStrayMarkdown(
  String(s == null ? '' : s).replace(/\*\*(.+?)\*\*/g, '$1'));

export function reportToText(report, payload, opts = {}) {
  if (!report) return '';
  const { pipelineLabel = 'All pipelines' } = opts;
  const out = [];

  out.push('PIPELINE BRIEF');
  /* ⚠ The SAME formatters the header uses — dates rather than "Custom
     range", and no seconds. A paste that labelled the window differently
     from the screen it was copied from is its own small lie. */
  const rangeLabel = formatRangeLabel(payload && payload.period);
  const headerBits = [pipelineLabel];
  if (rangeLabel) headerBits.push(rangeLabel);
  const stamp = formatGeneratedAt(payload && payload.generated_at);
  if (stamp) headerBits.push(`generated ${stamp}`);
  out.push(headerBits.join(' · '));
  out.push('');

  if (report.headline) { out.push(plain(report.headline)); out.push(''); }
  if (report.summary)  { out.push(plain(report.summary));  out.push(''); }

  const tiles = kpiTiles(payload);
  if (tiles.length) {
    const w = Math.max(...tiles.map((t) => t.label.length));
    for (const t of tiles) {
      const d = formatDelta(t.metric);
      /* ⚠ The same "why no arrow" line the tile shows. "No prior window" and
         "flat" are different facts in the paste too. */
      /* ⛔ A mapped caption or nothing — never the code, here either. */
      const caption = comparisonCaption(t.metric);
      const trail = d || (caption ? `(${caption})` : '(no change)');
      out.push(`  ${t.label.padEnd(w)}  ${formatMetric(t.metric)}  ${trail}`);
    }
    out.push('');
  }

  report.sections.forEach((s, i) => {
    out.push(`${i + 1}. ${plain(s.title)}${s.status ? `  [${s.status.toUpperCase()}]` : ''}`);
    s.bullets.forEach((b) => out.push(`   - ${plain(b)}`));
    if (s.chart) out.push(`   [chart: ${s.chart.type} — drawn from ${s.chart.metricKey}]`);
    out.push('');
  });

  if (report.risks.length) {
    out.push('RISKS');
    report.risks.forEach((r) => {
      out.push(`   - ${r.severity ? `${r.severity.toUpperCase()}: ` : ''}${plain(r.text)}`);
    });
    out.push('');
  }

  if (report.actions.length) {
    out.push('RECOMMENDED ACTIONS');
    report.actions.forEach((a) => {
      const meta = [a.owner, a.due].filter(Boolean).join(', ');
      out.push(`   [ ] ${plain(a.text)}${meta ? ` — ${meta}` : ''}`);
    });
    out.push('');
  }

  return out.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

export default reportToText;
